import {
  browserLocalPersistence,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
} from "firebase/auth";
import { Bot, ClipboardList, LogOut, Radio } from "lucide-react";
import { useEffect, useState } from "react";
import { AgentsView } from "./components/AgentsView";
import { LiveView } from "./components/LiveView";
import { LoginPage } from "./components/LoginPage";
import { TasksView } from "./components/TasksView";
import { auth, ensureAnonymousSession } from "./firebase";
import { useKitchenRuntime } from "./hooks/useKitchenRuntime";
import type { PageId } from "./types";

const navigation = [
  { id: "live" as const, label: "Live", icon: Radio },
  { id: "tasks" as const, label: "Tasks", icon: ClipboardList },
  { id: "agents" as const, label: "Agents", icon: Bot },
];

function KitchenApp({ user }: { user: User }) {
  const [page, setPage] = useState<PageId>("live");
  const runtime = useKitchenRuntime();

  return (
    <div className="app-shell">
      <header className="app-header">
        <button className="brand" type="button" onClick={() => setPage("live")}>
          <img src="/favicon.png" alt="" />
          <span>WeRHungry</span>
        </button>
        <nav aria-label="Main navigation">
          {navigation.map(({ id, label, icon: Icon }) => (
            <button
              type="button"
              key={id}
              className={page === id ? "active" : ""}
              onClick={() => setPage(id)}
              aria-current={page === id ? "page" : undefined}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </nav>
        <div className="restaurant-identity">
          <i />
          <div>
            <strong>Hong Kong Banjeom</strong>
            <span>{user.email ?? "Dinner shift"}</span>
          </div>
          <button
            className="sign-out"
            type="button"
            onClick={() => void signOut(auth)}
            aria-label="Sign out"
            title="Sign out"
          >
            <LogOut size={16} />
          </button>
        </div>
      </header>

      {page === "live" ? (
        <LiveView
          snapshot={runtime.snapshot}
          mode={runtime.mode}
          connected={runtime.connected}
          gestureHint={runtime.gestureHint}
          registerBandPress={runtime.registerBandPress}
          approve={runtime.approve}
          pass={runtime.pass}
          reset={runtime.reset}
        />
      ) : null}
      {page === "tasks" ? <TasksView snapshot={runtime.snapshot} /> : null}
      {page === "agents" ? (
        <AgentsView agents={runtime.agents} runs={runtime.snapshot.agentRuns} />
      ) : null}

      <nav className="mobile-nav" aria-label="Mobile navigation">
        {navigation.map(({ id, label, icon: Icon }) => (
          <button
            type="button"
            key={id}
            className={page === id ? "active" : ""}
            onClick={() => setPage(id)}
          >
            <Icon size={21} />
            <span>{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [authReady, setAuthReady] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(
    () =>
      onAuthStateChanged(auth, (nextUser) => {
        setUser(nextUser);
        setAuthReady(true);
      }),
    [],
  );

  const login = async (loginEmail = email, loginPassword = password) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      await setPersistence(auth, browserLocalPersistence);
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
    } catch {
      setAuthError("We could not sign you in. Check the email and password, then try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      await setPersistence(auth, browserLocalPersistence);
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch {
      setAuthError("Google sign-in is unavailable for this demo. Use the demo account below.");
    } finally {
      setAuthLoading(false);
    }
  };

  const openDemo = async () => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      await setPersistence(auth, browserLocalPersistence);
      await ensureAnonymousSession();
    } catch {
      setAuthError("The live demo could not be opened. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  const resetPassword = async () => {
    if (!email) {
      setAuthError("Enter your email address first, then request a password reset.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setAuthError("Password reset email sent.");
    } catch {
      setAuthError("We could not send a reset email for that address.");
    }
  };

  if (!authReady || !user) {
    return (
      <LoginPage
        email={email}
        password={password}
        loading={!authReady || authLoading}
        error={authError}
        onEmailChange={setEmail}
        onPasswordChange={setPassword}
        onSubmit={() => login()}
        onDemo={openDemo}
        onGoogle={loginWithGoogle}
        onForgotPassword={resetPassword}
      />
    );
  }

  return <KitchenApp user={user} />;
}
